import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { PawPrint, Menu, X } from "lucide-react";
import { useState } from "react";

const links = [
  { to: "/", label: "Home" },
  { to: "/quote", label: "Quick Quote" },
  { to: "/routes", label: "Routes" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const dashHref = user
    ? user.role === "admin"
      ? "/admin"
      : user.role === "driver"
      ? "/driver"
      : "/dashboard"
    : "/login";

  return (
    <header className="surface-glass sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 font-display text-lg" data-testid="navbar-logo">
          <img src="/brand/logo-round.png?v=5" alt="UK Pet Transport mascot" className="h-12 w-12 object-cover rounded-full border border-border shadow-md" />
          <span className="font-medium tracking-tight">UK PetTransport</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link to={dashHref} className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  logout();
                  nav("/");
                }}
                data-testid="nav-logout-btn"
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-login">
                Login
              </Link>
              <Button asChild size="sm" data-testid="nav-book-btn">
                <Link to="/book">Book Now</Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm py-2"
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to={dashHref} onClick={() => setOpen(false)} className="text-sm py-2">Dashboard</Link>
                <button
                  className="text-sm py-2 text-left"
                  onClick={() => {
                    logout();
                    setOpen(false);
                    nav("/");
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm py-2">Login</Link>
                <Link to="/book" onClick={() => setOpen(false)} className="text-sm py-2 text-primary font-medium">Book Now</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
